import { EventEmitter, Injectable } from '@angular/core';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { CustomerToken } from '../interfaces/customer-token.interface';
import { ApiService } from './api.service';
import { NotificationService } from '../../../shared/services/notification.service';

/**
 * gets, validates and stores a customers jwt token in a customers browser.
 * will call apis and emit the last updated token with the eventemitter, lets you read out the current state wint getToken
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerAuthService {
  private customerToken: CustomerToken;
  private customerTokenStorageName = 'customerToken';

  readonly updatedUserToken = new EventEmitter<CustomerToken | null>();
  readonly requestNewTokenPending = new EventEmitter<boolean>();
  readonly requestNewTokenSuccess = new EventEmitter<null>();
  readonly logoutEverywhereRequested = new EventEmitter<boolean>();
  readonly logoutEverywhereSuccessState = new EventEmitter<boolean>();
  readonly deleteAccountRequested = new EventEmitter<boolean>();
  readonly deleteAccountSuccessState = new EventEmitter<boolean>();

  constructor(
    private apiService: ApiService,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationService
  ) {
    this.customerToken = this.readToken();
  }

  requestNewToken(email: string) {
    this.requestNewTokenPending.emit(true);
    this.apiService.requestNewToken(email).subscribe(
      () => {
        this.requestNewTokenPending.emit(false);
        this.requestNewTokenSuccess.emit();
      },
      error => {
        console.error('could not request new token for email: ' + email, error);
        this.requestNewTokenPending.emit(false);
      }
    );
  }

  /**
   * starts a validation process of the given token against the server authority.
   * Will return the newest token info with the udpatedUserToken emitter.
   * @param token
   */
  validateToken(token) {
    this.apiService.validateCustomerToken(token).subscribe(
      (token: CustomerToken) => {
        this.updateToken(token);
      },
      error => {
        this.updateToken(null);
        if (error.status === 403) {
          console.error('could not validate token', error);
          this.notificationService.add({
            displayTime: 0,
            transkey: 'MYALPINRESORTS.failedLogin',
            id: 'blocker'
          });
        } else {
          console.error(error);
          this.notificationService.add({
            displayTime: 0,
            transkey: 'MYALPINRESORTS.errorGeneral',
            id: 'blocker'
          });
        }
      }
    );
  }

  getCurrentToken(): CustomerToken | null {
    return this.customerToken;
  }

  /**
   * removes token from browser, notifies every subscriber with an null token
   */
  logout() {
    this.updateToken(null);
  }

  /**
   * reads the customer token from the local storage, deserializes and patches values
   * @returns CustomerToken or null
   */
  private readToken(): CustomerToken | null {
    const token = this.localStorageService.get<CustomerToken>(this.customerTokenStorageName);
    if (token) {
      token.createdAt = new Date(token.createdAt);
      token.lastLogin = new Date(token.lastLogin);
    }
    return token;
  }

  /**
   * writes token to storage and emits to app
   * @param token the customer token
   */
  private updateToken(token: CustomerToken | null) {
    this.customerToken = token;
    this.localStorageService.write(this.customerTokenStorageName, JSON.stringify(token));
    this.updatedUserToken.emit(token);
  }

  /**
   * Logout user from current session.
   * Will fire logoutEverywhere emitter true.
   */
  private logOutUser() {
    this.logoutEverywhereSuccessState.emit(true);
    this.updateToken(null);
  }

  /**
   * invalidates all tokens from the user and removes token
   * emits the request and a success-state (false/true)
   */
  logoutEverywhere() {
    this.logoutEverywhereRequested.emit(true);
    this.apiService.logoutEverywhere().subscribe(
      () => {
        this.logoutEverywhereRequested.emit(false);
        this.logOutUser();
      },
      error => {
        console.error('Could not logout everywhere', error);
        this.logoutEverywhereRequested.emit(false);
        this.logoutEverywhereSuccessState.emit(false);
      }
    );
  }

  /**
   * Deletes the current user account and log out the user in the current session
   */
  deleteUserAccount() {
    this.deleteAccountRequested.emit(true);
    this.apiService.deleteUserAccount().subscribe(
      () => {
        this.deleteAccountRequested.emit(false);
        this.deleteAccountSuccessState.emit(true);
        this.logOutUser();
      },
      error => {
        console.error('Could not delete account', error);
        this.deleteAccountRequested.emit(false);
        this.deleteAccountSuccessState.emit(false);
      }
    );
  }
}
